import { useState, useEffect, useCallback } from 'react';
import { 
  DrawApiService, 
  DrawCurrentResponse, 
  DrawEligibility, 
  DrawHistoryResponse,
  DrawParticipateResponse 
} from '../services/drawApi';

/**
 * Hook for managing current draw data
 */
export function useCurrentDraw() {
  const [currentDraw, setCurrentDraw] = useState<DrawCurrentResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastErrorTime, setLastErrorTime] = useState<number | null>(null);

  const fetchCurrentDraw = useCallback(async () => {
    // Prevent rapid retries after errors - wait at least 60 seconds for current draw
    if (lastErrorTime && Date.now() - lastErrorTime < 60000) {
      console.log('Skipping current draw fetch due to recent error');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const draw = await DrawApiService.getCurrentDraw();
      setCurrentDraw(draw);
      setLastErrorTime(null); // Clear error time on success
    } catch (err: any) {
      console.error('Error fetching current draw:', err);
      const errorMessage = err.message || 'Failed to fetch current draw';
      setError(errorMessage);
      
      // Set error time for any error to trigger backoff
      setLastErrorTime(Date.now());
      console.log('Error detected for current draw, implementing backoff');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchCurrentDraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCurrentDraw]);

  return {
    currentDraw,
    loading,
    error,
    refetch: fetchCurrentDraw,
    hasRecentError: lastErrorTime !== null && Date.now() - lastErrorTime < 5000
  };
}

/**
 * Hook for checking draw eligibility
 */
export function useDrawEligibility() {
  const [eligibility, setEligibility] = useState<DrawEligibility | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastErrorTime, setLastErrorTime] = useState<number | null>(null);

  const clearEligibility = useCallback(() => {
    setEligibility(null);
  }, [setEligibility]);

  const checkEligibility = useCallback(async () => {
    // Prevent rapid retries after network errors - wait at least 1 minute
    if (lastErrorTime && Date.now() - lastErrorTime < 60000) {
      console.log('Skipping eligibility check due to recent error');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await DrawApiService.checkEligibility();
      setEligibility(result);
      setLastErrorTime(null); // Clear error time on success
    } catch (err: any) {
      console.error('Error checking eligibility:', err);
      const errorMessage = err.message || 'Failed to check eligibility';
      setError(errorMessage);
      setEligibility(null);
      
      // Set error time for any error to trigger backoff
      setLastErrorTime(Date.now());
      console.log('Error detected, implementing backoff');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    eligibility,
    loading,
    error,
    checkEligibility,
    clearEligibility,
    hasRecentError: lastErrorTime !== null && Date.now() - lastErrorTime < 60000
  };
}

/**
 * Hook for participating in draws
 */
export function useDrawParticipation() {
  const [participationResult, setParticipationResult] = useState<DrawParticipateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const participate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setParticipationResult(null);
    
    try {
      const result = await DrawApiService.participateInDraw();
      setParticipationResult(result);
      return result;
    } catch (err: any) {
      console.error('Error participating in draw:', err);
      const errorMessage = err.message || 'Failed to participate in draw';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setParticipationResult(null);
    setError(null);
  }, []);

  return {
    participationResult,
    loading,
    error,
    participate,
    reset
  };
}

/**
 * Hook for managing draw history
 */
export function useDrawHistory() {
  const [history, setHistory] = useState<DrawHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = useCallback(async (page: number = 1, limit: number = 90) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await DrawApiService.getDrawHistory(page, limit);
      setHistory(result);
    } catch (err: any) {
      console.error('Error fetching draw history:', err);
      setError(err.message || 'Failed to fetch draw history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  return {
    history,
    loading,
    error,
    refetch: fetchHistory
  };
}

/**
 * Combined hook for draw page data
 */
export function useDrawPageData() {
  const currentDraw = useCurrentDraw();
  const eligibility = useDrawEligibility();
  const participation = useDrawParticipation();
  const history = useDrawHistory();

  // Unified refresh function for all draw data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      currentDraw.refetch(),
      eligibility.checkEligibility()
    ]);
  }, [currentDraw, eligibility]);

  return {
    currentDraw,
    eligibility,
    participation,
    history,
    refreshAll
  };
}